import { Component, Inject } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { eachMonthOfInterval } from 'date-fns';
import * as _ from 'lodash';
import { GroupingSimulation } from 'src/app/models/ContractPrice';
import { ElectricityPriceWeighting } from '../hedging-view';

export interface BasePriceBreakdownData {
    periodLabel: string,
    description: string,
    deliveryStartDate: Date,
    deliveryEndDate: Date,
    // The equipments have no consumption in the delivery period, so their average month was used instead
    usesAverageConsumption: boolean,
    // Consumption of each equipment in the months of the offer's delivery period; estimated months have no record, so the equipment's average month is used
    recentConsumptions: { code: string, tariffCycle?: ElectricityPriceWeighting.TariffCycle, months: { month: Date, estimated: boolean, consumption: GroupingSimulation.IEquipmentConsumption }[] }[],
    // The offer's prices, in €/kWh
    offerPrices: ElectricityPriceWeighting.PeriodValues,
    breakdown: ElectricityPriceWeighting.PriceBreakdown
}

@Component({
    selector: 'app-base-price-breakdown',
    templateUrl: './base-price-breakdown.component.html',
    styleUrls: ['./base-price-breakdown.component.scss']
})
export class BasePriceBreakdownComponent {

    // In the order they're usually presented (most to least expensive)
    readonly periods: { key: ElectricityPriceWeighting.Period, labelKey: string }[] = [
        { key: 'peak', labelKey: 'INDEXED_PRICE_PERIOD_PEAK' },
        { key: 'standard', labelKey: 'INDEXED_PRICE_PERIOD_STANDARD' },
        { key: 'offPeak', labelKey: 'INDEXED_PRICE_PERIOD_OFF_PEAK' },
        { key: 'superOffPeak', labelKey: 'INDEXED_PRICE_PERIOD_SUPER_OFF_PEAK' }
    ];

    private readonly tariffCycleLabelKeys: { [cycle in ElectricityPriceWeighting.TariffCycle]: string } = {
        simple: 'BASE_PRICE_TARIFF_SIMPLE',
        biHourly: 'BASE_PRICE_TARIFF_BI_HOURLY',
        triHourly: 'BASE_PRICE_TARIFF_TRI_HOURLY',
        fourPeriods: 'BASE_PRICE_TARIFF_FOUR_PERIODS'
    };

    readonly data: BasePriceBreakdownData;
    readonly months: number;

    // Expandable sections: the base price is open by default
    private readonly openSections = new Set<string>(['final']);

    isOpen(section: string): boolean {
        return this.openSections.has(section);
    }

    toggle(section: string) {
        if (!this.openSections.delete(section)) {
            this.openSections.add(section);
        }
    }

    // Recorded consumptions grouped like the cost: one column per price slot of the equipment's tariff
    readonly recentTables: { code: string, columns: ElectricityPriceWeighting.Period[], rows: { date: Date, estimated: boolean, values: Partial<{ [period in ElectricityPriceWeighting.Period]: number }>, total: number }[] }[];
    // The same table summed over all the equipments; the months are estimated if any equipment's month is
    readonly totalTable: { columns: ElectricityPriceWeighting.Period[], rows: { date: Date, estimated: boolean, values: Partial<{ [period in ElectricityPriceWeighting.Period]: number }>, total: number }[] };
    // Periods the equipments' estimated consumption is priced in (all equipments), for the weighted average
    readonly totalPeriods: ElectricityPriceWeighting.Period[];

    constructor(
        private readonly dialogRef: MatDialogRef<BasePriceBreakdownComponent>,
        @Inject(MAT_DIALOG_DATA) data: BasePriceBreakdownData
    ) {
        this.data = data;
        this.months = this.getMonths(data.deliveryStartDate, data.deliveryEndDate);

        this.recentTables = data.recentConsumptions.map((recent) => {
            const rows = recent.months.map(({ month, estimated, consumption }) => {
                const slots = ElectricityPriceWeighting.getPriceSlots(data.offerPrices, consumption as unknown as ElectricityPriceWeighting.PeriodValues, recent.tariffCycle);
                return {
                    date: month,
                    estimated,
                    slots,
                    values: Object.fromEntries(slots.map((slot) => [slot.priceKey, slot.consumption])) as Partial<{ [period in ElectricityPriceWeighting.Period]: number }>,
                    total: consumption.totalValue
                };
            });

            return {
                code: recent.code,
                columns: this.periods.map((period) => period.key).filter((key) => rows.some((row) => row.slots.some((slot) => slot.priceKey == key))),
                rows
            };
        });

        // All equipments together, month by month, in the same format as each equipment's table
        const totalColumns = this.periods.map((period) => period.key).filter((key) => this.recentTables.some((table) => table.columns.includes(key)));
        const months = _.uniqBy(this.recentTables.flatMap((table) => table.rows), (row) => row.date.getTime()).map((row) => row.date);

        this.totalTable = {
            columns: totalColumns,
            rows: months.map((date) => {
                const monthRows = this.recentTables.flatMap((table) => table.rows.filter((row) => row.date.getTime() == date.getTime()));

                return {
                    date,
                    estimated: monthRows.some((row) => row.estimated),
                    values: Object.fromEntries(totalColumns.map((key) => [key, _.sumBy(monthRows, (row) => row.values[key] ?? 0)])) as Partial<{ [period in ElectricityPriceWeighting.Period]: number }>,
                    total: _.sumBy(monthRows, (row) => row.total)
                };
            })
        };

        this.totalPeriods = this.periods.map((period) => period.key)
            .filter((key) => data.breakdown.equipments.some((equipment) => equipment.slots.some((slot) => slot.priceKey == key)));
    }

    // Terms of the weighted average: total consumption (all equipments) in each priced period, and the period's price in €/MWh
    get weightedTerms(): { key: ElectricityPriceWeighting.Period, consumption: number, pricePerMWh: number }[] {
        return this.totalPeriods.map((key) => ({
            key,
            consumption: this.getTotalSlotConsumption(key),
            pricePerMWh: (Number(this.data.offerPrices[key]) || 0) * 1000
        }));
    }

    hasEstimatedRows(rows: { estimated: boolean }[]): boolean {
        return rows.some((row) => row.estimated);
    }

    // Estimated consumption (MWh) of an equipment in a price slot, undefined when its tariff has no such slot
    getSlotConsumption(equipment: ElectricityPriceWeighting.EquipmentBreakdown, key: ElectricityPriceWeighting.Period): number | undefined {
        return equipment.slots.find((slot) => slot.priceKey == key)?.consumption;
    }

    getTotalSlotConsumption(key: ElectricityPriceWeighting.Period): number {
        return this.data.breakdown.equipments.reduce((total, equipment) => total + (this.getSlotConsumption(equipment, key) ?? 0), 0);
    }

    // Only the periods the offer has a price for
    get pricedPeriods() {
        return this.periods.filter((period) => (Number(this.data.offerPrices[period.key]) || 0) > 0);
    }

    getPeriodLabelKey(key: ElectricityPriceWeighting.Period): string {
        return this.periods.find((period) => period.key == key)!.labelKey;
    }

    getTariffCycleLabelKey(tariffCycle?: ElectricityPriceWeighting.TariffCycle): string {
        return tariffCycle ? this.tariffCycleLabelKeys[tariffCycle] : 'BASE_PRICE_TARIFF_UNKNOWN';
    }

    private getMonths(start: Date, end: Date): number {
        const startDate = new Date(start);
        const endDate = new Date(end);

        return isNaN(startDate.getTime()) || isNaN(endDate.getTime()) || startDate > endDate ? 0 : eachMonthOfInterval({ start: startDate, end: endDate }).length;
    }

    close() {
        this.dialogRef.close();
    }
}
